import { inject, Injectable } from '@angular/core';
import {
  prepareINSERT,
  prepareUPDATE,
  prepareVALUES,
  TransactionDB
} from '@core/services/database/models/transaction.interface';
import { BaseDatabaseService } from '@core/services/database/base-database.service';
import { LoggerService } from '@core/services/logger/logger';
import { isPlatform } from '@ionic/angular';
import { v4 as uuid } from 'uuid';
import { environment } from '@env';

export interface SQLFilterTransactions {
  page?: number;
  limit?: number;
  created_from?: number | string;
  created_to?: number | string;
  is_tracking?: boolean;
  pump?: number;
  nozzle?: number;
  driver?: string;
  vehicle?: string;
  status?: 'finished' | 'error';
  sort?:
    | 'created'
    | 'updated'
    | 'transaction_id'
    | 'pump'
    | 'nozzle'
    | 'fuel_grade'
    | 'transaction_state'
    | 'filled_volume';
  order?: 'ASC' | 'DESC';
  sync_api?: number;
}

@Injectable({
  providedIn: 'root',
})
export class TransactionsServiceDB extends BaseDatabaseService {
  private loggerService = inject(LoggerService);

  constructor() {
    //override dbVerService: DbnameVersionService //override sqliteService: SQLiteService,
    super(/*sqliteService, dbVerService*/);
    this.exec = async () => {
      let query = `SELECT uuid FROM transactions WHERE uuid IS NULL LIMIT 1`;
      let res = await this.querySafe(query);
      const isUUID = !!res?.values?.length;
      if (!isUUID) {
        // Set uuid for all transactions
        query = `SELECT id FROM transactions WHERE uuid IS NULL`;
        res = await this.querySafe(query);
        console.log('res', res);
        if (res?.values?.length) {
          for (const item of res.values) {
            const { sql, values } = prepareUPDATE('transactions', {
              uuid: uuid()
            });
            await this.querySafe(`${sql} WHERE id=?`, [
              ...prepareVALUES(values),
              item.id
            ]);
          }
        }
      }

      console.log('isUUID', isUUID);
    };
  }

  async addTransaction(transaction: Partial<TransactionDB>) {
    transaction.uuid = uuid();
    const timestamp = new Date().getTime();
    transaction.created = timestamp;
    transaction.updated = timestamp;

    console.log('addTransaction', transaction);
    try {
      const { sql, values } = prepareINSERT('transactions', transaction);
      console.log('addTransaction', prepareVALUES(values));
      await this.querySafe(sql, prepareVALUES(values));
      if (!isPlatform('hybrid')) {
        await this.sqliteService.sqliteConnection.saveToStore(
          this.databaseName
        );
      }
      this.loggerService.info(
        'Add transaction',
        'addTransaction',
        `$sql: ${JSON.stringify(sql)}; values: ${JSON.stringify(values)} `
      );
      return true;
    } catch (e) {
      this.loggerService.error('Add transaction', 'addTransaction', e);
      throw Error('Error adding transaction');
    }
  }

  async updateTransaction(
    transactionId: TransactionDB['transaction_id'],
    pump: TransactionDB['pump'],
    transaction: Partial<TransactionDB>
  ): Promise<boolean> {
    if (!transactionId || !pump) {
      return false;
    }

    transaction.updated = new Date().getTime();

    const { sql, values } = prepareUPDATE('transactions', transaction);
    try {
      console.log(`${sql} WHERE transaction_id=? AND pump=?`, [
        ...prepareVALUES(values),
        transactionId,
        pump
      ]);
      await this.querySafe(`${sql} WHERE transaction_id=? AND pump=?`, [
        ...prepareVALUES(values),
        transactionId,
        pump
      ]);
      if (!isPlatform('hybrid')) {
        await this.sqliteService.sqliteConnection.saveToStore(
          this.databaseName
        );
      }
      this.loggerService.info(
        'Update transaction',
        'updateTransaction',
        `$sql: ${JSON.stringify(sql)}; values: ${JSON.stringify(values)} `
      );
      return true;
    } catch (e) {
      this.loggerService.error('Update transaction', 'updateTransaction', e);
      return false;
    }
  }

  async truncateTransactions(): Promise<boolean> {
    let query = 'DELETE FROM transactions';
    await this.querySafe(query);
    if (!isPlatform('hybrid')) {
      await this.sqliteService.sqliteConnection.saveToStore(this.databaseName);
    }
    return true;
  }

  async getWatchTransactions(): Promise<TransactionDB[]> {
    return this.getTransactions({ is_tracking: true });
  }

  getExportTransactions(
    params: SQLFilterTransactions = {}
  ): Promise<TransactionDB[]> {
    return this.#SQLTransactions<TransactionDB[]>(
      params,
      false,
      true
    ) as Promise<TransactionDB[]>;
  }

  getTransactions(
    params: SQLFilterTransactions = {}
  ): Promise<TransactionDB[]> {
    return this.#SQLTransactions<TransactionDB[]>(params) as Promise<
      TransactionDB[]
    >;
  }

  async getTransactionById(transaction_id: number) {
    const query = `SELECT * FROM transactions WHERE transaction_id = ${transaction_id}`;

    if (!environment.production) {
      console.log('getTransactionById', query);
    }

    let ret: any = await this.querySafe(query);
    if (ret.values.length > 0) {
      return ret.values[0] as TransactionDB;
    }
    return null;
  }

  async getTransactionByUuid(uuid: string) {
    const query = `SELECT * FROM transactions WHERE uuid = '${uuid}'`;

    if (!environment.production) {
      console.log('getTransactionByUuid', query);
    }

    let ret: any = await this.querySafe(query);

    if (ret.values.length > 0) {
      return ret.values[0] as TransactionDB;
    }
    return null;
  }

  getTransactionsCount(params: SQLFilterTransactions = {}): Promise<number> {
    return this.#SQLTransactions<number>(params, true);
  }

  async #SQLTransactions<T = TransactionDB[]>(
    params: SQLFilterTransactions = {},
    selectCount: boolean = false,
    forExport = false
  ): Promise<T> {
    params = { ...params };
    let page = 1;
    let limit = 100;
    let order = `${params.sort || 'created'} ${params.order || 'DESC'}`;

    if (params.limit) {
      limit = Math.max(1, params.limit);
    }

    if (params.page) {
      page = Math.max(1, params.page);
    }

    let sql_limit = !forExport ? ` LIMIT ${(page - 1) * limit}, ${limit}` : '';

    let isTrackingFilter = '';
    if (typeof params.is_tracking === 'boolean') {
      isTrackingFilter = ` AND is_tracking = ${params.is_tracking ? 1 : 0}`;
    }

    let pumpFilter = '';
    if (params.pump) {
      pumpFilter = ` AND pump = ${Number(params.pump)}`;
    }

    let nozzleFilter = '';
    if (params.nozzle) {
      nozzleFilter = ` AND nozzle = ${Number(params.nozzle)}`;
    }

    let syncApiFilter = '';
    if (typeof params.sync_api === 'number') {
      syncApiFilter = ` AND sync_api = ${Number(params.sync_api)}`;
    }

    let statusFilter = '';
    if (params.status) {
      switch (params.status) {
        case 'error':
          statusFilter = `AND transaction_state != 'Finished' AND transaction_state != 'Filling'`;
          break;
        case 'finished':
          statusFilter = `AND transaction_error_message IS NULL AND transaction_state = 'Finished'`;
          break;
      }
    }

    let driver = '';
    if (params.driver) {
      let driver = `driver LIKE '%"rfid_tag":"${params.driver}"%'`;
      const code = Number(params.driver);
      if (!isNaN(code)) {
        driver += ` OR driver LIKE '%"code":${code}%'`;
      }
    }

    let vehicle = '';
    if (params.vehicle) {
      let vehicle = `vehicle LIKE '%"rfid_tag":"${params.vehicle}"%'`;
      const code = Number(params.vehicle);
      if (!isNaN(code)) {
        vehicle += ` OR vehicle LIKE '%"code":${code}%'`;
      }
    }

    let createdFromFilter = '';
    if (params.created_from) {
      try {
        if (typeof params.created_from === 'string') {
          params.created_from = new Date(params.created_from).getTime();
        }
        createdFromFilter = ` AND created >= ${Number(params.created_from)}`;
      } catch (e) {
        this.loggerService.error('Transactions', '#SQLTransactions', e);
      }
    }

    let createdToFilter = '';
    if (params.created_to) {
      try {
        if (typeof params.created_to === 'string') {
          params.created_to = new Date(params.created_to).getTime();
        }
        createdToFilter = ` AND created <= ${Number(params.created_to)}`;
      } catch (e) {
        this.loggerService.error('Transactions', '#SQLTransactions', e);
      }
    }

    let query = '';
    if (selectCount) {
      query = `SELECT COUNT(*) as count`;
    } else {
      query = `SELECT *`;
    }

    // add from
    query += ` FROM transactions WHERE 1 `;

    // add filters
    query += ` ${isTrackingFilter} ${pumpFilter} ${nozzleFilter} ${statusFilter} ${createdFromFilter} ${createdToFilter} ${driver} ${vehicle} ${syncApiFilter}`;

    if (!selectCount) {
      // add order and limit
      query += ` ORDER BY ${order} ${sql_limit}`;
    }

    this.loggerService.info('Transactions', '#SQLTransactions', query);

    let res = await this.querySafe(query);

    if (selectCount) {
      if (res?.values?.length) {
        return res.values[0].count as T;
      }
      return 0 as T;
    }

    return res.values as T;
  }

  /**
   * Delete all Transactions
   * @returns
   */
  async deleteTransactions(): Promise<boolean> {
    await this.querySafe(`DELETE FROM transactions`);
    if (!isPlatform('hybrid')) {
      await this.sqliteService.sqliteConnection.saveToStore(this.databaseName);
    }
    return true;
  }
}
